import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface SectionProps {
  children: ReactNode;
  dark?: boolean;
  cream?: boolean;
  fullBleed?: boolean;
  grain?: boolean;
  narrow?: boolean;
  tinted?: boolean;
  cinematic?: boolean;
  animate?: boolean;
  className?: string;
  id?: string;
  tabIndex?: number;
  padding?: "default" | "sm" | "lg" | "xl";
}

const Section = ({
  children,
  dark,
  cream,
  fullBleed,
  grain: hasGrain,
  narrow,
  tinted,
  cinematic,
  animate = true,
  className = "",
  id,
  tabIndex,
  padding = "default",
}: SectionProps) => {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(!animate);

  useEffect(() => {
    if (!animate) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.04 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [animate]);

  const paddingMap = {
    xl: cinematic ? "section-cinematic" : "section-padding-xl",
    lg: "section-padding-lg",
    sm: "section-padding-sm",
    default: cinematic ? "section-padding-lg" : "section-padding",
  };
  const paddingClass = paddingMap[padding];

  return (
    <section
      ref={ref}
      id={id}
      tabIndex={tabIndex}
      style={{ willChange: animate && !visible ? "opacity, transform" : "auto" }}
      className={cn(
        fullBleed ? "" : paddingClass,
        fullBleed ? "" : narrow ? "container-narrow" : "container-wide",
        dark && "section-dark",
        cream && "section-cream",
        tinted && "section-tinted",
        hasGrain && "grain",
        animate && !visible && "opacity-0 translate-y-4",
        animate && visible && "animate-slide-up",
        className
      )}
    >
      {children}
    </section>
  );
};

export default Section;
